import sanitizeHtml from "sanitize-html";

import { RICH_TEXT_KEPT_ATTRIBUTES, RICH_TEXT_TAGS } from "@/lib/rich-text";

/**
 * Stored rich text, reduced to what it may render — **on render**, in the one
 * place the client turns a string into markup (`HtmlBox`).
 *
 * The donor sanitises nothing: its `HtmlBox` is a bare `dangerouslySetInnerHTML`
 * (`packages/react-ui/src/html-box/html-box.tsx:16-23`), so a community link's
 * description and a Vis Spec line's notes are stored XSS for every viewer of a
 * community page. The details grilling ruled them sanitised on render (#724,
 * D3); Python's `sanitize_rich_text` stays the boundary for the reads that run
 * it on the way out, and this is the same policy a second time, in front of
 * every field the box draws.
 *
 * **Beside `HtmlBox`, not in `lib/`**: only the box calls it, and `lib/`
 * admits a module both graphs import (`client/docs/architecture.md`, "The
 * dependency rule").
 *
 * **Isomorphic by construction.** `sanitize-html` parses with `htmlparser2` —
 * no DOM — so the Nitro server's markup and the hydrating browser's come out of
 * one function and agree, string for string. DOMPurify would need `jsdom` on
 * the server: two parsers for one string is a hydration mismatch waiting to
 * happen, and a hand-rolled walk (`scrubPastedHtml`) is browser-only.
 *
 * **The policy is `nh3`'s, mirrored** (`server/app/core/rich_text.py`):
 *
 *   - the tags are the stored allowlist and the attributes its one row — both
 *     imported from `lib/rich-text.ts`, which the contracts tier's drift test
 *     already pins to the Python module, so nothing here restates a list;
 *   - `target` survives only as the literal `_blank` (nh3's
 *     `tag_attribute_values`), and **every** `<a>` gets `rel="noopener
 *     noreferrer"`, replacing whatever the author wrote — nh3's `link_rel`
 *     stamps even an anchor with no `href`, so this does too;
 *   - `http`, `https`, `mailto` and `tel` hrefs, and relative ones; anything
 *     else (`javascript:`, `data:`) loses its `href` and keeps its text;
 *   - **no tag beyond the stored allowlist** — a ruled parity break (#741
 *     D2). The donor's editor can keep `blockquote`, `pre`, `code`, `figure`
 *     and `img` from a paste, and its `HtmlBox` draws them; here they are
 *     unwrapped like any unknown tag (an `img`, which has no text, simply
 *     goes). The prod dump showed nothing relying on them where this sanitiser
 *     decides: the community links carry `<p>` alone, and the fields Python
 *     already sanitises (the catalog, the spec-line notes) arrive without
 *     them. One policy on both sides, and no stored image fetched from a
 *     third-party host by every viewer;
 *   - an unknown tag is **unwrapped** — the tag goes, its text stays — except
 *     `script` and `style`, whose content goes too. Only those two: nh3 keeps a
 *     `textarea`'s or `noscript`'s text, and `sanitize-html`'s wider default
 *     would drop it.
 *
 * Probed against `nh3` on 38 fixtures: identical but for serialisation — `<br
 * />` for `<br>`, a decoded no-break space for `&nbsp;`, no written-out
 * implied `tbody` — and the browser parses each pair to the same DOM. The
 * fixtures are `sanitize-rich-text.unit.test.ts`.
 *
 * Idempotent, so a field Python already cleaned passes through unchanged.
 * Something that sanitises to nothing comes back `""`; whether that means
 * *absent* is the caller's question (`null`-on-empty is Python's rule for the
 * fields it serves).
 */
export function sanitizeRichText(html: string): string {
  return sanitizeHtml(html, POLICY);
}

const SAFE_REL = "noopener noreferrer";

const allowedAttributes = Object.fromEntries(
  Object.entries(RICH_TEXT_KEPT_ATTRIBUTES).map(([tag, names]) => [
    tag.toLowerCase(),
    // `rel` is kept only so the stamp below survives the attribute filter,
    // which runs after `transformTags`; its value is always this module's.
    tag.toLowerCase() === "a" ? [...names, "rel"] : [...names],
  ]),
);

const POLICY: sanitizeHtml.IOptions = {
  allowedTags: RICH_TEXT_TAGS.map((tag) => tag.toLowerCase()),
  allowedAttributes,
  allowedSchemes: ["http", "https", "mailto", "tel"],
  allowedSchemesAppliedToAttributes: ["href"],
  // Unwrap: the tag goes, its text stays — nh3's behaviour.
  disallowedTagsMode: "discard",
  nonTextTags: ["script", "style"],
  transformTags: {
    a: (tagName, attribs) => {
      const { target, ...rest } = attribs;
      return {
        tagName,
        attribs: {
          ...rest,
          ...(target === "_blank" ? { target } : {}),
          rel: SAFE_REL,
        },
      };
    },
  },
};
